import { useState } from "react"
import styled from "styled-components"
import { VehicleMedia } from "../../../components/VehicleMedia/VehicleMedia"
import type { ContactPreference, ContactValues } from "../types"

const WEEKDAYS = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
]
const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
]
const PREF_LABELS: Record<ContactPreference, string> = {
  email: "E-mail",
  phone: "Telefone",
  whatsapp: "WhatsApp",
}

/** "Terça-feira, 8 de setembro de 2026" */
const fullDateLabel = (isoDate: string): string => {
  const d = new Date(`${isoDate}T00:00:00`)
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`
}

// Copia com fallback: navigator.clipboard só existe em contexto seguro (HTTPS/
// localhost); o site publicado/preview roda em HTTP, onde cai no execCommand.
const copyText = (text: string): boolean => {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => undefined)
    return true
  }
  if (typeof document === "undefined") return false
  try {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.style.position = "fixed"
    ta.style.opacity = "0"
    document.body.appendChild(ta)
    ta.focus()
    ta.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
)

const CopiedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const CopyProtocol = ({ protocol }: { protocol: string }) => {
  const [copied, setCopied] = useState(false)
  return (
    <CopyButton
      type="button"
      aria-label={`Protocolo ${protocol}`}
      title={copied ? "Copiado" : "Copiar protocolo"}
      onClick={() => {
        if (copyText(protocol)) setCopied(true)
      }}
    >
      {copied ? <CopiedIcon /> : <CopyIcon />}
    </CopyButton>
  )
}

interface ConfirmationProps {
  protocol?: string
  date: string
  timeSlot: string
  slotDurationMinutes: number
  modelName: string
  versionName?: string
  cover?: string
  frames?: string[]
  contact: ContactValues
  unitName: string
  unitAddress?: string
  mapLink?: string
}

export const Confirmation = ({
  protocol,
  date,
  timeSlot,
  slotDurationMinutes,
  modelName,
  versionName,
  cover,
  frames = [],
  contact,
  unitName,
  unitAddress,
  mapLink,
}: ConfirmationProps) => (
  <Wrap data-testid="agendamento-test-drive-confirmation">
    <Header>
      <CheckCircle aria-hidden>
        <CheckIcon />
      </CheckCircle>
      <div>
        <HeaderTitle>Recebemos seu pedido de Test Drive</HeaderTitle>
        <HeaderText>
          A equipe da unidade entra em contato para confirmar a data e o horário.
        </HeaderText>
      </div>
    </Header>

    <Columns>
      <LeftCol>
        {(cover || frames.length > 0) && (
          <Media>
            <VehicleMedia
              frames={frames}
              cover={cover || undefined}
              alt={`${modelName}${versionName ? ` ${versionName}` : ""}`}
              maxHeight={{ mobile: "12rem", desktop: "14rem" }}
              resetSignal={`${modelName}-${versionName ?? ""}`}
              hint={{
                show: frames.length > 1,
                label: "Arraste para girar",
                color: "var(--text-body, #64748b)",
              }}
            />
          </Media>
        )}
        <VehicleNameRow>
          <VehicleName>{modelName}</VehicleName>
          {versionName && <VersionBadge>{versionName}</VersionBadge>}
        </VehicleNameRow>

        <Card>
          <BlockTitle>Pré-agendamento</BlockTitle>
          <PreDate>{fullDateLabel(date)}</PreDate>
          <TimeLine>
            <strong>{timeSlot}</strong>
            <span>· {slotDurationMinutes} min</span>
          </TimeLine>
          {protocol && (
            <ProtocolRow>
              <small>Protocolo</small>
              <code>{protocol}</code>
              <CopyProtocol protocol={protocol} />
            </ProtocolRow>
          )}
        </Card>
      </LeftCol>

      <RightCol>
        <Card>
          <BlockTitle>Dados pessoais</BlockTitle>
          <InfoList>
            <InfoRow>
              <span>Nome completo</span>
              <strong>{contact.name}</strong>
            </InfoRow>
            <InfoRow>
              <span>E-mail</span>
              <strong>{contact.email}</strong>
            </InfoRow>
            <InfoRow>
              <span>WhatsApp</span>
              <strong>{contact.whatsapp}</strong>
            </InfoRow>
            {contact.preferences.length > 0 && (
              <InfoRow>
                <span>Contato por</span>
                <strong>
                  {contact.preferences.map((p) => PREF_LABELS[p]).join(", ")}
                </strong>
              </InfoRow>
            )}
          </InfoList>
        </Card>

        <Card>
          <BlockTitle>Onde será</BlockTitle>
          <UnitName>{unitName}</UnitName>
          {unitAddress && <UnitAddress>{unitAddress}</UnitAddress>}
          {mapLink && (
            <MapLink href={mapLink} target="_blank" rel="noopener noreferrer">
              <MapPinIcon />
              Ver no mapa
            </MapLink>
          )}
        </Card>
      </RightCol>
    </Columns>
  </Wrap>
)

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`
const Header = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`
const CheckCircle = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--general-primary, #0027d4);
  color: var(--buttons-text, #fff);

  svg {
    width: 20px;
    height: 20px;
  }
`
const HeaderTitle = styled.strong`
  display: block;
  font-size: 1.25rem;
  color: var(--text-title, #0f172a);
`
const HeaderText = styled.span`
  color: var(--text-body, #64748b);
  font-size: 0.875rem;
`
const Columns = styled.div`
  display: grid;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1.1fr 1fr;
    align-items: start;
  }
`
const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`
const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
const Media = styled.div`
  width: 100%;
`
const VehicleNameRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`
const VehicleName = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--td-step-title, var(--text-title, #0f172a));
`
const VersionBadge = styled.span`
  font-size: 0.875rem;
  color: var(--td-step-subtitle, var(--text-body, #64748b));
`
const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 12px;
  background: var(--cards-background, #ffffff);
  border: 1px solid var(--cards-border, var(--general-border, #e2e8f0));
`
const BlockTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--general-primary, #0027d4);
`
const PreDate = styled.strong`
  font-size: 1rem;
  color: var(--cards-title, var(--text-title, #0f172a));
`
const TimeLine = styled.p`
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  color: var(--cards-title, var(--text-title, #0f172a));

  strong {
    font-size: 1.0625rem;
  }

  span {
    font-size: 0.875rem;
    color: var(--text-body, #64748b);
  }
`
const ProtocolRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;

  small {
    font-size: 0.75rem;
    color: var(--text-body, #64748b);
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--cards-title, var(--text-title, #0f172a));
  }
`
const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: var(--general-primary, #0027d4);
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`
const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`
const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  span {
    font-size: 0.75rem;
    color: var(--text-body, #64748b);
  }

  strong {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--cards-title, var(--text-title, #0f172a));
    word-break: break-word;
  }
`
const UnitName = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--cards-title, var(--text-title, #0f172a));
`
const UnitAddress = styled.span`
  font-size: 0.8125rem;
  color: var(--text-body, #64748b);
`
const MapLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: fit-content;
  margin-top: 0.5rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid var(--cards-border, var(--general-border, #b7c0cd));
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--cards-title, var(--text-title, #0f172a));
  transition: background 0.15s ease;

  &:hover {
    background: var(--general-background, #f1f5f9);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`
